import { useEffect, useState } from 'react';
import { Search, X, Calendar } from 'lucide-react';
import type { AgedStockFilterOptions, AgedStockFilters, AgeBucket, ExpiryStatus } from '../../types/agedStock';

interface AgedStockFiltersProps {
    filters: AgedStockFilters;
    filterOptions: AgedStockFilterOptions | null;
    onFilterChange: (filters: AgedStockFilters) => void;
}

const AGE_BUCKETS: AgeBucket[] = ['0-90', '91-180', '181-365', '366-720', '>720'];
const EXPIRY_STATUSES: { value: ExpiryStatus; label: string }[] = [
    { value: 'expired', label: 'Expired' },
    { value: 'expiringSoon', label: 'Expiring < 90d' },
    { value: 'active', label: 'Active' },
    { value: 'noData', label: 'No expiry data' },
];

export function AgedStockFiltersView({ filters, filterOptions, onFilterChange }: AgedStockFiltersProps) {
    const [searchInput, setSearchInput] = useState(filters.search ?? '');
    const [lotInput, setLotInput] = useState(filters.lotSearch ?? '');

    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchInput !== (filters.search ?? '')) {
                onFilterChange({ ...filters, search: searchInput || undefined });
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handle = setTimeout(() => {
            if (lotInput !== (filters.lotSearch ?? '')) {
                onFilterChange({ ...filters, lotSearch: lotInput || undefined });
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [lotInput]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = (patch: Partial<AgedStockFilters>) => onFilterChange({ ...filters, ...patch });

    const clearAll = () => {
        setSearchInput('');
        setLotInput('');
        // Preserve "Only with lot" preference — clearing filters shouldn't unhide pseudo-lots.
        onFilterChange({ onlyWithLot: filters.onlyWithLot ?? true });
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
            {/* Row 1: Organisation filters */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                <select
                    value={filters.companyCode ?? ''}
                    onChange={e => update({ companyCode: e.target.value || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Companies</option>
                    {filterOptions?.companies.map(c => (
                        <option key={c.code} value={c.code}>{c.name || c.code}</option>
                    ))}
                </select>

                <select
                    value={filters.businessUnit ?? ''}
                    onChange={e => update({ businessUnit: e.target.value || undefined, parentCategory: undefined, category: undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Business Units</option>
                    {filterOptions?.businessUnits.map(b => (
                        <option key={b} value={b}>{b}</option>
                    ))}
                </select>

                <select
                    value={filters.parentCategory ?? ''}
                    onChange={e => update({ parentCategory: e.target.value || undefined, category: undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Parent Categories</option>
                    {filterOptions?.parentCategories.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                </select>

                <select
                    value={filters.category ?? ''}
                    onChange={e => update({ category: e.target.value || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Categories</option>
                    {filterOptions?.categories
                        .filter(c => !filters.parentCategory || c.parentCode === filters.parentCategory)
                        .map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                </select>

                <select
                    value={filters.warehouseCountry ?? ''}
                    onChange={e => update({ warehouseCountry: e.target.value || undefined, warehouseCode: undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Countries</option>
                    {filterOptions?.warehouseCountries.map(c => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>

                <select
                    value={filters.warehouseCode ?? ''}
                    onChange={e => update({ warehouseCode: e.target.value || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Warehouses</option>
                    {filterOptions?.warehouses
                        .filter(w => !filters.warehouseCountry || w.country === filters.warehouseCountry)
                        .map(w => (
                            <option key={w.code} value={w.code}>{w.name}</option>
                        ))}
                </select>
            </div>

            {/* Row 2: Date / Age / Expiry / Flag filters */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                        type="date"
                        value={filters.purchaseDateFrom ?? ''}
                        onChange={e => update({ purchaseDateFrom: e.target.value || undefined })}
                        className="w-full rounded-md border border-gray-300 pl-8 pr-2 py-1.5 text-sm"
                        placeholder="Purchased from"
                    />
                </div>
                <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                        type="date"
                        value={filters.purchaseDateTo ?? ''}
                        onChange={e => update({ purchaseDateTo: e.target.value || undefined })}
                        className="w-full rounded-md border border-gray-300 pl-8 pr-2 py-1.5 text-sm"
                        placeholder="Purchased to"
                    />
                </div>

                <select
                    value={filters.ageBucket ?? ''}
                    onChange={e => update({ ageBucket: (e.target.value as AgeBucket) || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Age Buckets</option>
                    {AGE_BUCKETS.map(b => (
                        <option key={b} value={b}>{b} days</option>
                    ))}
                </select>

                <input
                    type="number"
                    value={filters.minAgeDays ?? ''}
                    onChange={e => update({ minAgeDays: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Min age (days)"
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />

                <select
                    value={filters.expiryStatus ?? ''}
                    onChange={e => update({ expiryStatus: (e.target.value as ExpiryStatus) || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">Any Expiry Status</option>
                    {EXPIRY_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>

                <select
                    value={filters.onlyFlagged ?? ''}
                    onChange={e => update({ onlyFlagged: (e.target.value as 'expired' | 'qualityIssue') || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    <option value="">All Lots</option>
                    <option value="expired">Only flagged: Expired</option>
                    <option value="qualityIssue">Only flagged: Quality</option>
                </select>
            </div>

            {/* Row 3: Search + supplier */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                <div className="relative col-span-1 sm:col-span-1 lg:col-span-2">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        placeholder="Search product name / code / supplier"
                        className="w-full rounded-md border border-gray-300 pl-8 pr-2 py-1.5 text-sm"
                    />
                </div>
                <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={lotInput}
                        onChange={e => setLotInput(e.target.value)}
                        placeholder="Search Lot Code"
                        className="w-full rounded-md border border-gray-300 pl-8 pr-2 py-1.5 text-sm"
                    />
                </div>

                <select
                    value={filters.vendorID ?? ''}
                    onChange={e => update({ vendorID: e.target.value || undefined })}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    title="Actual vendor the lot was purchased from (per Lot's latest Purchase transaction)"
                >
                    <option value="">All Vendors</option>
                    {filterOptions?.vendors.map(v => (
                        <option key={v.code} value={v.code}>{v.name}</option>
                    ))}
                </select>
            </div>

            {/* Active filter chips + only-with-lot toggle + clear */}
            <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                    <span>
                        {Object.entries(filters).filter(([, v]) => v != null && v !== '' && v !== false).length} filters active
                    </span>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="When checked, hides synthetic '(unaccounted)' pseudo-lots that exist only to reconcile the total. Uncheck to see all stock including stock without a lot number.">
                        <input
                            type="checkbox"
                            checked={filters.onlyWithLot !== false}
                            onChange={e => update({ onlyWithLot: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-gray-300"
                        />
                        Only with lot
                    </label>
                </div>
                <button
                    type="button"
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-50"
                >
                    <X className="h-3 w-3" /> Clear All
                </button>
            </div>
        </div>
    );
}
